import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { db } from '../../firebase';
import { ref, onValue } from 'firebase/database';
import { 
    FaArrowLeft, 
    FaSort,
    FaSearch,
    FaPrint,
    FaFileExcel,
    FaCopy
} from 'react-icons/fa';

const AuditRecommendationList = () => {
    const navigate = useNavigate();
    const location = useLocation();
    
    // Determine if we are looking at Engineers or Societies based on the path
    const isSociety = location.pathname.includes('recommendation-list-society');
    const pageTitle = isSociety ? "मजूर संख्या यादी" : "सु.बे. यादी";

    const [works, setWorks] = useState([]);
    const [selectedTaluka, setSelectedTaluka] = useState('');
    const [appliedTaluka, setAppliedTaluka] = useState('');
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    // Hardcoded Marathi Taluka List for Sangli district
    const talukaList = [
        { en: 'Shirala', mr: 'शिराळा' },
        { en: 'Walwa', mr: 'वाळवा' },
        { en: 'Tasgaon', mr: 'तासगाव' },
        { en: 'Khanapur', mr: 'खानापूर (विटा)' },
        { en: 'Atpadi', mr: 'आटपाडी' },
        { en: 'Kavathe Mahankal', mr: 'कवठे महांकाळ' },
        { en: 'Miraj', mr: 'मिरज' },
        { en: 'Palus', mr: 'पलूस' },
        { en: 'Jat', mr: 'जत' },
        { en: 'Kadegaon', mr: 'कडेगाव' },
        { en: 'Mukhyalaya', mr: 'मुख्यालय' }
    ];

    useEffect(() => {
        setLoading(true);

        // Fetch works from generatework node (snapshot of works used in lotteries)
        const generateWorkRef = ref(db, 'ekamwatapzp/generatework');
        onValue(generateWorkRef, (snapshot) => {
            if (snapshot.exists()) {
                const datesData = snapshot.val();
                let allFlattenedWorks = [];

                // Traverse through each date node
                Object.values(datesData).forEach(dateSnapshot => {
                    // Traverse inside each date node to find works (either direct or nested in categories/meetings)
                    const traverseNode = (node) => {
                        if (!node || typeof node !== 'object') return;
                        
                        if (node.schemeName || node.workName) {
                            // This is a work object
                            allFlattenedWorks.push({
                                ...node,
                                id: node.id || node.workNumber || Math.random().toString(36).substr(2, 9)
                            });
                        } else {
                            // Recursive traversal for nested meeting/category folders
                            Object.values(node).forEach(child => traverseNode(child));
                        }
                    };

                    traverseNode(dateSnapshot);
                });

                // Filter by type (Engineer vs Society)
                // Society works typically have sharedwork as "मजूर"
                const filteredByType = allFlattenedWorks.filter(work => {
                    const isWorkSociety = (work.category || '').toLowerCase().includes('society') || 
                                         (work.registrationType || '').toLowerCase().includes('society') ||
                                         (work.sharedwork || '').includes('मजूर');
                    return isSociety === isWorkSociety;
                });

                // Deduplicate works by workNumber (as same work might appear in different nodes if re-added)
                const uniqueWorks = [];
                const seenNumbers = new Set();
                filteredByType.forEach(w => {
                    if (w.workNumber && !seenNumbers.has(w.workNumber)) {
                        uniqueWorks.push(w);
                        seenNumbers.add(w.workNumber);
                    } else if (!w.workNumber) {
                        uniqueWorks.push(w);
                    }
                });

                setWorks(uniqueWorks);
            }
            setLoading(false);
        });

    }, [isSociety]);

    const handleSearch = () => {
        setAppliedTaluka(selectedTaluka);
    };

    const normalizeTaluka = (name) => {
        if (!name) return "";
        const n = name.toString().toLowerCase().trim();
        
        // Dictionary for mapping common variations to standardized English keys
        const standardMap = {
            'shirala': 'shirala', 'शिराळा': 'shirala',
            'walwa': 'walwa', 'वाळवा': 'walwa', 'islampur': 'walwa',
            'tasgaon': 'tasgaon', 'तासगाव': 'tasgaon',
            'khanapur': 'khanapur', 'खानापूर': 'khanapur', 'vita': 'khanapur', 'खानापूर (विटा)': 'khanapur',
            'atpadi': 'atpadi', 'आटपाडी': 'atpadi',
            'kavathe mahankal': 'kavathe mahankal', 'कवठे महांकाळ': 'kavathe mahankal', 'kavathemahangal': 'kavathe mahankal', 'कवठेमहांकाळ': 'kavathe mahankal',
            'miraj': 'miraj', 'मिरज': 'miraj', 'sangli': 'miraj',
            'palus': 'palus', 'पलूस': 'palus',
            'jat': 'jat', 'जत': 'jat', 'jath': 'jat',
            'kadegaon': 'kadegaon', 'कडेगाव': 'kadegaon',
            'mukhyalaya': 'mukhyalaya', 'मुख्यालय': 'mukhyalaya'
        };

        return standardMap[n] || n;
    };

    const getMarathiTaluka = (enName) => {
        if (!enName) return "-";
        const normalizedEn = normalizeTaluka(enName);
        const found = talukaList.find(t => normalizeTaluka(t.en) === normalizedEn);
        return found ? found.mr : enName;
    };

    // Filter Data
    const displayData = useMemo(() => {
        let filtered = works;

        if (appliedTaluka) {
            const standardizedApplied = normalizeTaluka(appliedTaluka);
            filtered = filtered.filter(w => {
                const standardizedWork = normalizeTaluka(w.taluka);
                return standardizedWork === standardizedApplied;
            });
        }

        if (searchTerm) {
            filtered = filtered.filter(w => 
                (w.workName && w.workName.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (w.workNumber && w.workNumber.toString().includes(searchTerm)) ||
                (w.schemeName && w.schemeName.toLowerCase().includes(searchTerm.toLowerCase()))
            );
        }

        return filtered;
    }, [works, appliedTaluka, searchTerm]);

    const formatCurrency = (amount) => {
        if (!amount) return "0.00";
        return new Intl.NumberFormat('en-IN', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(parseFloat(amount));
    };

    if (loading) {
        return (
            <div className="p-20 flex flex-col items-center justify-center space-y-4 font-marathi">
                <div className="animate-spin text-[#085e53] text-4xl">
                    <FaSort />
                </div>
                <p className="text-gray-400 font-bold animate-pulse uppercase tracking-widest text-xs">माहिती लोड होत आहे...</p>
            </div>
        );
    }

    return (
        <div className="p-4 md:p-8 bg-gray-50/50 min-h-screen font-marathi">
            {/* Header Section */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4 px-2 no-print">
                <div className="flex items-center gap-4">
                    <button 
                        onClick={() => navigate(-1)}
                        className="p-2.5 bg-white rounded-xl shadow-sm border border-gray-100 text-gray-400 hover:text-[#085e53] transition active:scale-95"
                    >
                        <FaArrowLeft />
                    </button>
                    <h1 className="text-2xl font-bold text-gray-800 tracking-tight">{pageTitle}</h1>
                </div>
                <div className="flex items-center gap-2 text-[12px] font-bold text-gray-400 uppercase tracking-wider">
                    सांगली जिल्हा परिषद / <span className="text-gray-600 font-black">ऑडिट विभाग / {pageTitle}</span>
                </div>
            </div>

            {/* Filter Card */}
            <div className="bg-white rounded shadow-sm border border-gray-100 p-8 mb-8 no-print border-t-4 border-t-[#085e53]">
                <div className="flex flex-col gap-3">
                    <label className="text-sm font-bold text-gray-700">
                        तालुका निवडा <span className="text-red-500">*</span>
                    </label>
                    <div className="flex flex-col md:flex-row gap-4 max-w-2xl">
                        <select 
                            className="flex-grow bg-white border border-gray-200 rounded py-2.5 px-4 focus:ring-2 focus:ring-[#085e53]/20 outline-none transition font-medium"
                            value={selectedTaluka}
                            onChange={(e) => setSelectedTaluka(e.target.value)}
                        >
                            <option value="">तालुका निवडा</option>
                            {talukaList.map(t => (
                                <option key={t.en} value={t.en}>{t.mr}</option>
                            ))}
                        </select>
                        <button 
                            onClick={handleSearch}
                            className="bg-[#085e53] hover:bg-[#064a41] text-white px-10 py-2.5 rounded font-bold shadow-md transition transform active:scale-95 whitespace-nowrap"
                        >
                            Search
                        </button>
                    </div>
                </div>
            </div>

            {/* Table Controls */}
            <div className="bg-white rounded-t border border-gray-100 p-4 border-b-0 flex flex-col md:flex-row justify-between items-center gap-4 no-print">
                <div className="flex items-center gap-1">
                    <button className="bg-[#6c757d] text-white px-4 py-1.5 rounded text-sm font-bold hover:bg-gray-700 transition flex items-center gap-2">
                        <FaCopy /> Copy
                    </button>
                    <button className="bg-[#6c757d] text-white px-4 py-1.5 rounded text-sm font-bold hover:bg-gray-700 transition flex items-center gap-2">
                        <FaFileExcel /> Excel
                    </button>
                    <button onClick={() => window.print()} className="bg-[#6c757d] text-white px-4 py-1.5 rounded text-sm font-bold hover:bg-gray-700 transition flex items-center gap-2">
                        <FaPrint /> Print
                    </button>
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-gray-600">Search:</span>
                    <input 
                        type="text"
                        className="border border-gray-300 rounded px-3 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-[#085e53]"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            {/* Main Table */}
            <div className="bg-white rounded-b shadow-sm border border-gray-100 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead className="bg-[#f8f9fa] border-b border-gray-200 text-center">
                            <tr className="text-[14px] font-bold text-gray-700">
                                <th colSpan="10" className="px-4 py-2 border-b border-gray-200 uppercase tracking-wider">बांधकाम उपविभाग पंचायत समिती, सांगली</th>
                            </tr>
                            <tr className="text-[12px] font-bold text-gray-600">
                                <th colSpan="10" className="px-4 py-4 border-b border-gray-200">
                                    जिल्हा परिषद काम वाटप समिती पुढे शिफारस करावयाची यादी दि. {new Date().toLocaleDateString('mr-IN')} रोजी घ्यावयाची कामे
                                </th>
                            </tr>
                            <tr className="text-[11px] font-black text-gray-500 uppercase tracking-tight">
                                <th className="px-3 py-4 border-r border-gray-100 w-12 text-center">अ.क्र.</th>
                                <th className="px-3 py-4 border-r border-gray-100 w-20 text-center">वर्क नंबर</th>
                                <th className="px-3 py-4 border-r border-gray-100 w-24">तालुका</th>
                                <th className="px-3 py-4 border-r border-gray-100 min-w-[250px]">कामाचे नाव</th>
                                <th className="px-3 py-4 border-r border-gray-100 w-24 text-center">मुदत (महिने)</th>
                                <th className="px-3 py-4 border-r border-gray-100 w-32 text-center">प्रशासकीय मंजुरी</th>
                                <th className="px-3 py-4 border-r border-gray-100 w-32 text-center">तांत्रिक मंजुरी</th>
                                <th className="px-3 py-4 border-r border-gray-100 w-24 text-center">र.रु. (लाखात)</th>
                                <th className="px-3 py-4 border-r border-gray-100 min-w-[200px]">योजनेचे नाव</th>
                                <th className="px-3 py-4 w-24 text-center">शेरा</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 text-[13px]">
                            {displayData.map((item, index) => (
                                <tr key={index} className="hover:bg-emerald-50/5 transition-colors">
                                    <td className="px-3 py-5 border-r border-gray-50 text-center font-bold text-gray-400">{index + 1}</td>
                                    <td className="px-3 py-5 border-r border-gray-50 text-center font-bold text-gray-700">{item.workId || item.workNumber || "-"}</td>
                                    <td className="px-3 py-5 border-r border-gray-50 font-bold text-gray-700">{getMarathiTaluka(item.taluka)}</td>
                                    <td className="px-3 py-5 border-r border-gray-50 font-bold text-gray-800 leading-relaxed text-xs">
                                        {item.workName}
                                    </td>
                                    <td className="px-3 py-5 border-r border-gray-50 text-center font-bold text-gray-700">{item.workDuration || "3 महिने"}</td>
                                    <td className="px-3 py-5 border-r border-gray-50 text-center">
                                        <div className="flex flex-col gap-1 text-[11px] font-bold text-gray-600">
                                            <span>{item.inwardNo || item.aaNo || item.aaNumber || "-"}</span>
                                            <span>{item.date || item.aaDate || "-"}</span>
                                        </div>
                                    </td>
                                    <td className="px-3 py-5 border-r border-gray-50 text-center">
                                        <div className="flex flex-col gap-1 text-[11px] font-bold text-gray-600">
                                            <span>{item.technicalApprovalOrderNo || item.tsNo || item.tsNumber || "-"}</span>
                                            <span>{item.inwardDate || item.tsDate || "-"}</span>
                                        </div>
                                    </td>
                                    <td className="px-3 py-5 border-r border-gray-50 text-center font-bold text-gray-700">
                                        {item.tenderAmount || item.amount ? formatCurrency(item.tenderAmount || item.amount) : "-"}
                                    </td>
                                    <td className="px-3 py-5 border-r border-gray-50 font-bold text-gray-700 text-xs">
                                        {item.schemeName}
                                    </td>
                                    <td className="px-3 py-5 text-center font-bold text-gray-400">-</td>
                                </tr>
                            ))}
                            {displayData.length === 0 && (
                                <tr>
                                    <td colSpan="10" className="px-6 py-20 text-center text-gray-400 font-bold italic">
                                        माहिती उपलब्ध नाही. कृपया तालुका निवडून सर्च करा.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <style>{`
                .font-marathi {
                    font-family: 'Hind', sans-serif;
                }
                @import url('https://fonts.googleapis.com/css2?family=Hind:wght@400;500;600;700&display=swap');
                @media print {
                    .no-print { display: none !important; }
                    body { background: white !important; padding: 0; margin: 0; }
                    .bg-white { box-shadow: none !important; border: none !important; }
                    table { border: 1px solid #ddd !important; border-collapse: collapse !important; width: 100% !important; }
                    th, td { border: 1px solid #ddd !important; padding: 8px !important; }
                }
            `}</style>
        </div>
    );
};

export default AuditRecommendationList;
